<template>
  <base-modal-ex :active.sync="isActive"
    :animation-in="animationIn"
    :animation-out="animationOut"
    :animation-duration="animationDuration"
    :can-cancel="false"
    :min-width="60"
    :min-height="modalMinHeight"
    has-modal-card>
    <div class="model-content">
      <div class="modal-card"
        :style="{ minHeight: modalMinHeight + 'vh' }">
        <header class="modal-card-head">
          <p class="modal-card-title">
            <slot name="text-title" />
          </p>
        </header>
        <section class="modal-card-body"
          ref="modalBody">
          <div class="columns">
            <div class="column">
              <div class="field">
                <label class="label is-required-label">Quote Template Name</label>
                <input v-model="templateName"
                  :class="[
                    'input is-uppercase',
                    {
                      'is-danger': $v.templateName.$dirty && (!$v.templateName.required || !$v.templateName.unique) && templateName
                    }
                  ]"
                  type="text"
                  placeholder="Template Name"
                  maxlength="30">
                <div class="help is-secondary">
                  {{ templateName.length }}/30
                </div>

                <span v-if="$v.templateName.$dirty && !$v.templateName.required"
                  class="help is-danger ml-1">Name Required</span>
                <span v-if="$v.templateName.$dirty && !$v.templateName.unique && templateName"
                  class="help is-danger ml-1">Name has been taken.</span>
              </div>
            </div>
            <div class="column is-two-fifths">
              <div class="field">
                <label class="label">Add Tag</label>
                <div class="control"
                  style="position: relative">
                  <div class="dropdown"
                    :class="{ 'is-active': tagDropdownActive }"
                    style="width: 100%">
                    <div class="dropdown-trigger"
                      style="width: 100%">
                      <div class="input tag-input-fake"
                        @click="toggleDropdown">
                        <template v-if="selectedTags.length">
                          <span v-for="tag in selectedTags"
                            :key="tag.tagID"
                            class="tag-short mr-1"
                            :style="{ backgroundColor: tag.colorHex, color: getTextColor(tag.colorHex), marginBottom: '2px' }">
                            {{ tag.shortTag }}
                          </span>
                        </template>
                        <span v-if="!selectedTags.length"
                          style="color: #b5b5b5">Add tags</span>
                        <span class="icon is-right"
                          style="pointer-events: none; position: absolute; right: 10px; top: 10px">
                          <i class="mdi mdi-menu-down" />
                        </span>
                      </div>
                    </div>
                    <TagsDropdownMenu :tags="tags"
                      :selected-tags="selectedTagIDs"
                      :is-visible="tagDropdownActive"
                      @toggle-tag="toggleTag"
                      @clear-all-tags="clearAllTags" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="field">
            <label class="label is-required-label">Template Type</label>
            <div class="select is-fullwidth">
              <select v-model="quoteType">
                <option v-for="(label, key) in availableQuoteTypes"
                  :key="key"
                  :value="key">{{ label }}</option>
              </select>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot"
          :style="`justify-content: flex-${buttonLocation}`">
          <button class="button is-primary"
            @click="update">Update</button>
          <button class="button"
            @click="close">Close</button>
        </footer>
      </div>
    </div>
  </base-modal-ex>
</template>
<script>
import BaseModalEx from '@/components/BulmaModal/BaseModalEx'
import QuoteTemplateService from '@/services/QuoteTemplateService'
import { required } from 'vuelidate/lib/validators'
import TagsDropdownMenu from '../../views/manageTag/TagsDropdownMenu.vue'
import { getTextColor } from '@/utils/ColorFunctions'
import { QuoteTemplateTypes } from '@/enums'

export default {
  name: 'QuoteTemplateEditModal',
  components: {
    BaseModalEx,
    TagsDropdownMenu
  },
  props: {
    active: {
      type: Boolean,
      default: false
    },
    animationIn: {
      type: String,
      default: 'fadeIn'
    },
    animationOut: {
      type: String,
      default: 'fadeOut'
    },
    animationDuration: {
      type: [String, Number],
      default: 0.3
    },
    buttonLocation: {
      type: String,
      default: 'end'
    },
    template: {
      type: Object,
      default: null
    },
    templates: {
      type: Array,
      default: () => []
    },
    tags: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isActive: this.active || false,
      templateName: null,
      selectedTagIDs: [],
      tagDropdownActive: false,
      modalMinHeight: 30,
      validationTimeout: null,
      quoteType: null,
      initialQuoteType: null
    }
  },
  computed: {
    templateNames() {
      return this.templates.filter((template) => template.templateId !== this.template?.templateId).map((template) => template.name.toUpperCase())
    },
    selectedTags() {
      return this.tags.filter((tag) => this.selectedTagIDs.includes(tag.tagID))
    },
    availableQuoteTypes() {
      if (this.initialQuoteType === 'I') return QuoteTemplateTypes
      return Object.fromEntries(Object.entries(QuoteTemplateTypes).filter(([key]) => key !== 'I'))
    }
  },
  validations: {
    templateName: {
      required,
      unique(value) {
        return !this.templateNames.includes(value.toUpperCase())
      }
    }
  },
  watch: {
    active(value) {
      this.isActive = value
    },
    templateName: {
      handler: function (val) {
        if (val) {
          // Debounce validation to avoid too many checks while typing
          clearTimeout(this.validationTimeout)
          this.validationTimeout = setTimeout(() => {
            this.$v.templateName.$touch()
          }, 300)
        } else {
          // Clear validation state when field is empty
          this.$v.templateName.$reset()
        }
      },
      immediate: false
    }
  },
  created() {
    if (this.template) {
      if (this.template.tags) this.selectedTagIDs = this.template.tags.map((tag) => tag.tagID)
      this.templateName = this.template.name
      this.quoteType = this.template.quoteType ?? 'Q'
      this.initialQuoteType = this.template.quoteType ?? 'Q'
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
    if (this.validationTimeout) {
      clearTimeout(this.validationTimeout)
    }
  },
  methods: {
    close() {
      this.isActive = false
      this.$emit('update:active', false)
    },
    async update() {
      this.$v.$touch()
      if (this.$v.$error) return
      this.template.name = this.templateName.toUpperCase()
      this.template.tags = this.selectedTags
      this.template.quoteType = this.quoteType
      this.$showSpinner()
      await QuoteTemplateService.updateQuoteTemplate(this.template)
      this.close()
      this.$hideSpinner()
    },
    toggleTag(tag) {
      const idx = this.selectedTagIDs.indexOf(tag.tagID)
      if (idx === -1) {
        this.selectedTagIDs.push(tag.tagID)
      } else {
        this.selectedTagIDs.splice(idx, 1)
      }
    },
    getTextColor,
    toggleDropdown() {
      this.tagDropdownActive = !this.tagDropdownActive
      this.$nextTick(() => {
        this.adjustModalHeight()
      })
    },
    handleClickOutside(event) {
      if (this.tagDropdownActive) {
        const dropdown = this.$el.querySelector('.dropdown')
        if (dropdown && !dropdown.contains(event.target)) {
          this.tagDropdownActive = false
          this.adjustModalHeight()
        }
      }
    },
    adjustModalHeight() {
      const body = this.$refs.modalBody
      if (body && this.tagDropdownActive) {
        const totalHeightPx = body.scrollHeight + 160
        const vh = (totalHeightPx / window.innerHeight) * 100
        this.modalMinHeight = Math.min(vh, 90)
      } else {
        this.modalMinHeight = 30
      }
    },
    clearAllTags() {
      this.selectedTagIDs = []
    }
  }
}
</script>

<style scoped  lang="scss">
.modal-card {
  width: 60vw;
  min-width: 800px;
}
</style>
